import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Clock, Eye, PlayCircle, MessageSquare } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { API_URL } from "../config";
import { parseDescription } from "../lectureText";
import { AuthorLink } from "../authorLookup";
import { recordWatch, setPosition } from "../watchHistory";
import ShareButtons from "./ShareButtons";
import CommentSection from "./CommentSection";
import LikeButton from "./LikeButton";
import RatingWidget from "./RatingWidget";
import SubscribeButton from "./SubscribeButton";
import BookmarkButton from "./BookmarkButton";
import ArticleAudioButton from "./ArticleAudioButton";
import CaseQuizPlayer from "./CaseQuizPlayer";

const KIND_CONFIG = {
  articles: { endpoint: "posts", label: "статья", listPath: "/", bodyField: "content", commentType: "post" },
  cases:    { endpoint: "cases", label: "клинический случай", listPath: "/cases", bodyField: "content", commentType: "case" },
  lectures: { endpoint: "lectures", label: "материал", listPath: "/lectures", bodyField: "description", commentType: "lecture" },
};

/**
 * Определяет embed-src для YouTube или VK Video.
 * Для VK нужна именно embed-ссылка с параметром hash (vk.com/video_ext.php?oid=...&id=...&hash=...) —
 * её выдаёт VK при действии «Поделиться → Встроить», обычная ссылка на видео для встраивания не подходит
 * (hash — одноразовый токен именно для внешнего плеера, не выводится из адреса страницы видео).
 * Поддерживаем и вставку целого <iframe> кода от VK (так его обычно и копируют), и голого URL.
 */
function resolveEmbedSrc(raw) {
  if (!raw) return null;
  let urlStr = raw.trim();

  const iframeMatch = urlStr.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
  if (iframeMatch) urlStr = iframeMatch[1];

  let u;
  try { u = new URL(urlStr); } catch { return null; }

  if (u.hostname.includes("youtube") || u.hostname.includes("youtu.be")) {
    const videoId = u.searchParams.get("v") || u.pathname.replace("/", "");
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  }

  if ((u.hostname.includes("vk.com") || u.hostname.includes("vkvideo.ru")) && u.pathname.includes("video_ext.php")) {
    return u.toString();
  }

  return null;
}

function extractFirstImage(markdown) {
  const m = (markdown || "").match(/!\[[^\]]*\]\((https?:\/\/[^)\s]+)\)/);
  return m ? m[1] : null;
}

function plainSnippet(markdown, length = 200) {
  const plain = (markdown || "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#*_`>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return plain.slice(0, length);
}

function VideoEmbed({ url, start }) {
  if (!url) return null;
  let src = resolveEmbedSrc(url);
  if (src && start) src += `${src.includes("?") ? "&" : "?"}t=${start}s&autoplay=1`;
  if (!src) {
    if (!/^https?:\/\//i.test(url.trim())) return null; // вставлен нераспознанный код, не ссылка — молча пропускаем
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#007c84", fontWeight: 600 }}>
        <PlayCircle size={16} strokeWidth={2.2} /> Смотреть видео →
      </a>
    );
  }
  return (
    <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: 10, overflow: "hidden", margin: "14px 0" }}>
      <iframe style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
        key={src}
        src={src}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; screen-wake-lock"
        allowFullScreen title="lecture-video" />
    </div>
  );
}

export default function ContentDetail({ kind, onLoginRequest }) {
  const cfg = KIND_CONFIG[kind];
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchParams] = useSearchParams();
  const [start, setStart] = useState(Number(searchParams.get("t")) || 0);   // ?t=секунды — переход с «Продолжить просмотр»
  const playerRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(`${API_URL}/${cfg.endpoint}/${id}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setItem)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [cfg.endpoint, id]);

  useEffect(() => { if (kind === "lectures" && item) recordWatch(item); }, [kind, item]);

  if (loading) return <section><p style={{ color: "#888" }}>Загрузка…</p></section>;
  if (error || !item) return (
    <section>
      <h2 className="section-title">Материал не найден</h2>
      <button onClick={() => navigate(cfg.listPath)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <ArrowLeft size={15} strokeWidth={2.2} /> К списку
      </button>
    </section>
  );

  const body = item[cfg.bodyField] || "";
  const tags = item.tags ? item.tags.split(";").map(t => t.trim()).filter(Boolean) : [];
  const author = item.is_anonymous ? "Аноним" : item.author;
  const description = plainSnippet(body);
  const ogImage = extractFirstImage(body) || `${window.location.origin}/logo512.png`;

  return (
    <section style={{ maxWidth: 760 }}>
      <Helmet>
        <title>{item.title} — Современный Врач</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={item.title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Link to={cfg.listPath} style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#888", textDecoration: "none", fontSize: "0.9rem" }}>
        <ArrowLeft size={14} strokeWidth={2.2} /> К списку
      </Link>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "12px 0 6px" }}>
        {item.status && (
          <span style={{
            background: item.status === "resolved" ? "#15803d" : "#007c84", color: "#fff",
            borderRadius: 6, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 600,
          }}>{item.status === "resolved" ? "Решён" : "Обсуждается"}</span>
        )}
        {item.material_type && (
          <span style={{ background: "#e5005b", color: "#fff", borderRadius: 6, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 600 }}>
            {item.material_type}
          </span>
        )}
        {item.duration && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem", color: "#007c84", fontWeight: 600 }}>
            <Clock size={13} strokeWidth={2.2} /> {item.duration}
          </span>
        )}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: "0.85rem", color: "#aaa", marginLeft: "auto" }}>
          {item.created_at ? new Date(item.created_at).toLocaleDateString("ru") : ""}
          {typeof item.views === "number" && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
              <Eye size={13} strokeWidth={2} /> {item.views}
            </span>
          )}
        </span>
      </div>

      <h1 style={{ fontSize: "1.6rem", color: "#007c84", margin: "0 0 8px", lineHeight: 1.25 }}>
        {item.title}
      </h1>

      {author && <div style={{ fontSize: "0.9rem", color: "#888", marginBottom: 6 }}>Автор: <AuthorLink name={author} style={{ color: "#007c84", fontWeight: 600 }} /></div>}

      {tags.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {tags.map(t => (
            <span key={t} style={{ background: "#e8edf8", color: "#007c84", borderRadius: 5, padding: "1px 8px", fontSize: "0.78rem" }}>{t}</span>
          ))}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <LikeButton contentType={cfg.commentType} contentId={Number(id)} onLoginRequest={onLoginRequest} />
        <BookmarkButton contentType={cfg.commentType} contentId={Number(id)} onLoginRequest={onLoginRequest} />
        {kind === "articles" && <ArticleAudioButton postId={Number(id)} />}
        <button
          onClick={() => navigate("/discussions", { state: { prefillText: `${window.location.origin}/${kind}/${id}` } })}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "#f0f2f8", color: "#555", border: "none", borderRadius: 8,
            padding: "6px 14px", fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
          }}
        >
          <MessageSquare size={15} strokeWidth={2.2} /> Обсудить
        </button>
        {author && !item.is_anonymous && (
          <SubscribeButton author={author} onLoginRequest={onLoginRequest} />
        )}
      </div>

      {kind === "lectures" && (
        <>
          <div ref={playerRef}><VideoEmbed url={item.video_url} start={start} /></div>
          {item.video_url && typeof item.views === "number" && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85rem", color: "#888", margin: "-6px 0 10px" }}>
              <Eye size={14} strokeWidth={2} /> {item.views} просмотров
            </div>
          )}
        </>
      )}

      {kind === "lectures" ? (() => {
        const { intro, chapters } = parseDescription(body);
        return (
          <>
            {intro && (
              <div className="article-body" style={{ marginTop: 8 }}>
                <ReactMarkdown remarkPlugins={[remarkBreaks, remarkGfm]}>{intro}</ReactMarkdown>
              </div>
            )}
            {chapters.length > 0 && (
              <div className="chapters">
                <h3 className="chapters-title">Содержание</h3>
                <ol className="chapters-list">
                  {chapters.map(c => (
                    <li key={c.time + c.title}>
                      <button type="button" className="chapter-btn" onClick={() => { setStart(c.seconds); setPosition(item.id, c.seconds, c.title); playerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }}>
                        <span className="chapter-time">{c.time}</span>
                        <span className="chapter-name">{c.title}</span>
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </>
        );
      })() : (
        <div className="article-body" style={{ marginTop: 8 }}>
          <ReactMarkdown remarkPlugins={[remarkBreaks, remarkGfm]}>{body}</ReactMarkdown>
        </div>
      )}

      <ShareButtons path={`/${kind}/${id}`} title={item.title} />

      {kind === "cases" && <CaseQuizPlayer caseId={Number(id)} />}

      {kind === "lectures" && (
        <RatingWidget contentType="lecture" contentId={Number(id)} onLoginRequest={onLoginRequest} />
      )}

      <CommentSection contentType={cfg.commentType} contentId={Number(id)} onLoginRequest={onLoginRequest} />
    </section>
  );
}
